import { IFeature, isSPFeatureEnabled } from './FeaturesBasic';

export { IFeature } from './FeaturesBasic';

/**
 * This file contains the known list of features (a.k.a. flights or ramps).
 * The signature of one entry is:
 *  static FeatureName: IFlight = { ODB: 100, ODC: "foo", Fallback: true };
 * ODB            Is the numerical value of the flight from the ExpFeature enumeration.
 *                Omit if the flight does not have a correspondent in ODB.
 * ODC            Is the string identifier in ODC.
 *                Omit if the flight does not have a correspondent ramp in ODC.
 * OneDrive       Is the value of the flight in OneDrive.
 *                Omit if the flight does not have a correspondent ramp in OneDrive.
 * Fallback       The state of the flight, true = on false = off.
 *                The fallback is used only when the ODB or ODC flight is not applicable.
 *                For example: The Flight is an ODB flight with no ODC counterpart and the app runs in ODC mode.
 *                The fallback also allows declaring app only switches that are not known to the backend.
 *                Omit if false.
 */

declare const window: {
    PageContext?: {};
    Flight?: {
        Ramps?: {
            [flight: string]: boolean;
        }
    };
    FilesConfig?: {
        [flight: string]: boolean;
    };
    Flighting?: {
        ExpFeatures?: number[];
    };
    _spPageContextInfo?: {
        ExpFeatures?: number[];
    }
};

let _microServicePageContext: typeof window.PageContext;
let _odcRamps: typeof window.Flight.Ramps;
let _odcConfig: typeof window.FilesConfig;
let _expFeatures: number[] | undefined;

let _initialized = false;

function initialize() {
    // OneDrive.com initialization
    _microServicePageContext = window.PageContext;

    // ODC initialization
    const odcFlightInfo = window.Flight;
    _odcRamps = odcFlightInfo && odcFlightInfo.Ramps;
    _odcConfig = window.FilesConfig;

    // SP/ODB: for backward compatibility,
    // if _spPageContextInfo is still available
    // this performs the initialization of the _expFeatures.
    const flighting = window.Flighting; // Old SharePoint pages use this.
    const spPageContextInfo = window._spPageContextInfo;

    if (!_expFeatures && spPageContextInfo) {
        if (!spPageContextInfo.ExpFeatures &&
            flighting && flighting.ExpFeatures) {
            spPageContextInfo.ExpFeatures = flighting.ExpFeatures;
        }
        Features.initSPExpFeatures(spPageContextInfo.ExpFeatures);
    }

    _initialized = true;
}

export default class Features {
    /* This feature is always on */
    public static On: IFeature = { Fallback: true };

    /* This feature is always off */
    public static Off: IFeature = {};

    //You should not add features here but in your own file

    /**
     * Performs the initialization of the client side features for
     * SP and ODB in scenarios where the window['_spPageContextInfo']
     * is not available.
     *
     * @param {number[]} expFeatures - The bit map of the client side features
     * usually provided by the ISPPageContext.
     *
     * @example initSPExpFeatures(pageContextInfo.ExpFeatures)
     */
    public static initSPExpFeatures(expFeatures: number[]) {
        _expFeatures = expFeatures;
    }

    /**
     * This function will return true when the feature is enabled and
     * will check the proper config for SP,ODB and/or ODC to determine.
     *
     * @requires In scenarios where the window['_spPagecontextInfo'] might not exist
     * this function requires initSPExpFeatures before checing the state of ODB features.
     *
     * @param {IFeature} feature - the feature to be checked if enabled.
     * @return {boolean} - True if the feature is found active.
     */
    public static isFeatureEnabled(feature: IFeature): boolean {
        // Avoid performing expensive initialization logic multiple times
        if (!_initialized) {
            initialize();
        }

        let result = !!feature.Fallback;

        const {
            ODB,
            ODC,
            OneDrive
        } = feature;

        if (ODB !== undefined && _expFeatures) {
            result = isSPFeatureEnabled(feature, _expFeatures);
        }

        if (ODC !== undefined && _odcConfig) {
            if (typeof ODC === 'boolean') {
                result = ODC;
            } else if (_odcRamps && _odcRamps[ODC]) {
                result = true;
            } else if (_odcConfig && _odcConfig[ODC]) {
                result = true;
            }
        }

        if (OneDrive && _microServicePageContext) {
            result = OneDrive;
        }

        return result;
    }
}
